// Inhalte der interaktiven Hilfe-Touren (Klick aufs Fragezeichen-Symbol neben dem Dark-Mode-
// Toggle in Layout.js, bzw. im Scouter-Dialog). Bewusst in derselben "du"-Stimme wie das
// User-Handbuch (docs/pv-userhandbuch.tex) geschrieben — kurze, direkte Sätze, für Leute, die die
// Oberfläche zum ersten Mal sehen. `target` ist ein CSS-Selektor, meist ein schon vorhandenes
// data-testid oder eine stabile id; `before` (optional) führt eine Aktion auf derselben Seite aus
// (z.B. einen Tab umschalten), bevor der Schritt angezeigt wird — nie eine Navigation zu einer
// anderen Route und nie ein automatisches Öffnen fremder Dialoge, das hält die Tour-Engine einfach
// (siehe TourOverlay.js).
const click = (selector) => () => document.querySelector(selector)?.click();
// Checkbox der ersten Kontaktzeile — Ziel & Trick für den Mehrfachauswahl-Schritt der
// Kontakte-Tour: E-Mail/MailerLite/Feld setzen etc. sind erst im DOM, sobald mindestens ein
// Kontakt markiert ist (siehe ContactsPage.js, `{selectedIds.size > 0 && (...)}`). Ohne diesen
// Trick würde die Tour an der Stelle ins Leere laufen, weil es dort schlicht nichts zu zeigen gäbe.
const FIRST_ROW_CHECKBOX = '[data-testid="contact-row-0"] button[role="checkbox"]';

export const TOURS = [
  {
    id: 'contacts',
    label: 'Kontakte',
    route: '/contacts',
    // Räumt die Beispielauswahl aus dem Mehrfachauswahl-Schritt wieder weg, egal wie die Tour
    // endet (Fertig, X, Esc, Klick daneben, Seitenwechsel) — siehe TourOverlay.js.
    cleanup: () => document.querySelector(`${FIRST_ROW_CHECKBOX}[data-state="checked"]`)?.click(),
    steps: [
      {
        target: '[data-testid="search-input"]',
        title: 'Suche',
        body: 'Hier tippst du einen Namen, ein Medium, einen Ort oder eine E-Mail-Adresse ein und bestätigst mit Enter. Kurze, eindeutige Begriffe liefern meist die besten Treffer.',
      },
      {
        target: '[data-testid="filter-btn"]',
        title: 'Filter',
        body: 'Über Filter kombinierst du mehrere Kriterien gleichzeitig — praktisch, um genau die richtige Zielgruppe einzugrenzen. Mehrere aktive Filter wirken gemeinsam.',
      },
      {
        target: '[data-testid="column-config-btn"]',
        title: 'Sichtbare Spalten',
        body: 'Hier blendest du Tabellenspalten ein oder aus. Deine Auswahl und Reihenfolge werden persönlich für dich gespeichert.',
      },
      {
        target: '[data-testid="duplicates-btn"]',
        title: 'Duplikate',
        body: 'Zeigt dir mögliche doppelte Kontakte an. Schau dir beide Datensätze genau an, bevor du etwas löschst — der PV führt nichts automatisch zusammen.',
      },
      {
        target: '[data-testid="sheet-tabs"]',
        title: 'Sheets',
        body: 'Sheets sind deine gespeicherten Arbeitsansichten — eigene Spaltenauswahl und Filter für wiederkehrende Aufgaben. Unten wechselst du zwischen ihnen.',
      },
      {
        target: '[data-testid="add-contact-btn"]',
        title: 'Kontakt anlegen',
        body: 'Legt einen komplett neuen Kontakt an. Tipp: Erst kurz nach der E-Mail-Adresse suchen, damit du niemanden doppelt anlegst.',
      },
      {
        target: '[data-testid="edit-btn-0"]',
        title: 'Kontakt bearbeiten',
        body: 'Das Stift-Symbol öffnet den ganzen Kontakt zum Bearbeiten. Für kleine Korrekturen genügt ein Doppelklick direkt in eine Tabellenzelle — ein einzelner Klick wählt die Zelle nur an.',
      },
      {
        target: '[data-testid="notes-btn-0"]',
        title: 'Notizzettel',
        body: 'Die Sprechblase öffnet die Notizen zu diesem Kontakt — für Gesprächsergebnisse, Rückmeldungen und nächste Schritte.',
      },
      {
        target: '[data-testid="contact-row-menu-0"]',
        title: 'Drei-Punkte-Menü',
        body: 'Hier findest du weitere Aktionen zu diesem Kontakt: E-Mail senden, die vom Scouter gefundenen Pressemeldungen ansehen und Löschen.',
      },
      {
        target: '[data-testid="import-btn"]',
        title: 'Import',
        body: 'Liest eine Tabelle (z. B. Excel) ein und ordnet ihre Spalten den PV-Feldern zu. Nur auf größeren Bildschirmen sichtbar.',
      },
      {
        target: '[data-testid="export-btn"]',
        title: 'Export',
        body: 'Gibt alle Kontakte als Excel-Datei aus. Da Exporte personenbezogene Daten enthalten: nur zweckgebunden speichern und teilen.',
      },
      {
        // Löst die Auswahl aus, bevor der Schritt angezeigt wird — sonst gäbe es hier (und in den
        // nächsten beiden Schritten) noch gar nichts zum Zeigen, siehe FIRST_ROW_CHECKBOX oben.
        target: FIRST_ROW_CHECKBOX,
        title: 'Mehrfachauswahl',
        body: 'Über die Kontrollkästchen markierst du mehrere Kontakte auf einmal. Ich hab dir schon einen markiert — sobald mindestens einer ausgewählt ist, erscheint oben eine Aktionsleiste mit E-Mail, MailerLite, Feld setzen und mehr.',
        // Nur anklicken, wenn noch nicht markiert — falls der Kontakt schon vorher ausgewählt
        // war (z.B. eigene Auswahl vor Tour-Start), soll die Tour sie nicht versehentlich aufheben.
        before: () => {
          const checkbox = document.querySelector(FIRST_ROW_CHECKBOX);
          if (checkbox && checkbox.getAttribute('data-state') !== 'checked') checkbox.click();
        },
      },
      {
        target: '[data-testid="show-selected-only-btn"]',
        title: 'Nur ausgewählte anzeigen',
        body: 'Blendet die Tabelle auf genau deine Auswahl herunter — praktisch, um sie vor einer Aktion nochmal isoliert zu kontrollieren.',
      },
      {
        target: '[data-testid="email-btn"]',
        title: 'E-Mail',
        body: 'Öffnet den E-Mail-Dialog für alle markierten Kontakte — mit Vorlage, Betreff, Nachricht und Vorschau vor dem Senden.',
      },
      {
        target: '[data-testid="mailerlite-btn"]',
        title: 'MailerLite',
        body: 'Übergibt die markierten Kontakte an die eingerichtete MailerLite-Zielgruppe.',
      },
      {
        target: '[data-testid="undo-btn"]',
        title: 'Rückgängig',
        body: 'Macht deine letzte Änderung rückgängig — auch per Strg+Z (Cmd+Z auf dem Mac). Wiederholen holt sie mit Strg+Y zurück.',
      },
    ],
  },
  {
    id: 'pressespiegel',
    label: 'Pressespiegel',
    route: '/pressespiegel',
    steps: [
      {
        target: '[data-testid="pressespiegel-search-input"]',
        title: 'Suche',
        body: 'Durchsucht Titel, Medium und Journalist*in. Leer das Feld und bestätige mit Enter, um die Suche wieder aufzuheben.',
      },
      {
        target: '[data-testid="pressespiegel-filter-btn"]',
        title: 'Filter',
        body: 'Grenzt die Liste nach Medium, Anlass, Journalist*in oder Zeitraum ein.',
      },
      {
        target: '[data-testid="pressespiegel-columns-btn"]',
        title: 'Spalten',
        body: 'Blendet Spalten ein oder aus — genau wie bei den Kontakten.',
      },
      {
        target: '[data-testid="pressespiegel-new-entry-btn"]',
        title: 'Neuer Eintrag',
        body: 'Legt einen Pressespiegel-Eintrag von Hand an. Liegt eine URL vor, kannst du Titel, Medium und Datum automatisch übernehmen lassen — prüf die Angaben danach trotzdem gegen die Originalquelle.',
      },
      {
        target: '[data-testid="scouter-open-btn"]',
        title: 'Scouter öffnen',
        body: 'Der Scouter sucht automatisch nach Medienberichten über ARIWA und schlägt sie dir zur Übernahme vor, statt dass du von Hand suchen musst. Klick auf das Fragezeichen im Scouter-Fenster für eine eigene Tour dazu.',
      },
      {
        target: '[data-testid="pressespiegel-table"]',
        title: 'Einträge',
        body: 'Als Editor*in klickst du einen Eintrag an, um ihn zu bearbeiten. Auf dem Handy siehst du statt der Tabelle kompakte Karten mit denselben Angaben.',
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    route: '/dashboard',
    // "Widget"/"Eigenes Widget"/"Standard-Dashboard" existieren nur im Bearbeiten-Modus (siehe
    // DashboardPage.js, `{editMode && !isMobile && (...)}`) — ohne diesen Trick würden die
    // nächsten drei Schritte ins Leere laufen. Am Ende wieder ausschalten, damit die Tour die
    // Seite nicht im Bearbeitungsmodus zurücklässt.
    cleanup: () => {
      const button = document.querySelector('[data-testid="edit-mode-btn"]');
      if (button?.textContent.includes('Fertig')) button.click();
    },
    steps: [
      {
        target: '[data-testid^="widget-"]',
        title: 'Kennzahlen',
        body: 'Kacheln, Diagramme und weitere Widgets zeigen dir deinen aktuellen Datenbestand. Viele lassen sich anklicken — du landest dann direkt bei der passenden Kontaktmenge.',
      },
      {
        target: '[data-testid="edit-mode-btn"]',
        title: 'Bearbeiten',
        body: 'Schaltet den Bearbeitungsmodus ein: Widgets verschieben, vergrößern oder entfernen, und neue hinzufügen. Deine Anordnung wird nur für dich gespeichert. Ich schalte ihn dir schon mal ein, damit ich dir zeigen kann, was dann dazukommt.',
        before: () => {
          const button = document.querySelector('[data-testid="edit-mode-btn"]');
          if (button?.textContent.includes('Bearbeiten')) button.click();
        },
      },
      {
        target: '[data-testid="add-widget-btn"]',
        title: 'Widget',
        body: 'Zeigt dir vorgefertigte Bausteine, die du deinem Dashboard hinzufügen kannst — nur im Bearbeiten-Modus sichtbar.',
      },
      {
        target: '[data-testid="create-kpi-btn"]',
        title: 'Eigenes Widget',
        body: 'Baust du dir eine eigene Kennzahl: Kennzahltyp wählen, optional Filter setzen, mit Vorschau prüfen und speichern.',
      },
      {
        target: '[data-testid="reset-dashboard-btn"]',
        title: 'Standard-Dashboard',
        body: 'Setzt dein Dashboard wieder auf die ursprüngliche Anordnung zurück, falls du dich beim Anpassen verzettelt hast.',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Einstellungen',
    route: '/settings',
    steps: [
      {
        target: '[data-testid="settings-tab-fields"]',
        title: 'Felder',
        body: 'Hier legst du fest, welche Angaben ein Kontakt überhaupt haben kann — neue Felder anlegen, bestehende bearbeiten oder umsortieren.',
        before: click('[data-testid="settings-tab-fields"]'),
      },
      {
        target: '[data-testid="settings-tab-pressespiegel"]',
        title: 'Pressespiegel',
        body: 'Genau wie bei den Kontakten verwaltest du hier die Felder, die ein Pressespiegel-Eintrag haben kann.',
        before: click('[data-testid="settings-tab-pressespiegel"]'),
      },
      {
        target: '[data-testid="settings-tab-email-templates"]',
        title: 'E-Mail-Vorlagen',
        body: 'Wiederkehrende Betreffzeilen und Nachrichtentexte speicherst du hier als Vorlage — beim Verfassen einer E-Mail wählst du sie dann einfach aus, statt jedes Mal neu zu schreiben.',
        before: click('[data-testid="settings-tab-email-templates"]'),
      },
      {
        target: '[data-testid="settings-tab-experience"]',
        title: 'Darstellung',
        body: 'Farbschema, Startseite, Tabellenkompaktheit und ein bevorzugter Sheet — diese Einstellungen gelten nur für dein eigenes Konto.',
        before: click('[data-testid="settings-tab-experience"]'),
      },
      {
        target: '[data-testid="settings-tab-security"]',
        title: 'Sicherheit',
        body: 'Passwort ändern und die Zwei-Faktor-Authentifizierung (2FA) einrichten oder verwalten.',
        before: click('[data-testid="settings-tab-security"]'),
      },
      {
        target: '[data-testid="settings-tab-logs"]',
        title: 'Protokoll',
        body: 'Zeigt dir, wer wann welche Änderung gemacht hat — mit Bereichen für Änderungen, wiederherstellbare Versionen und Sync-/Importvorgänge.',
        before: click('[data-testid="settings-tab-logs"]'),
      },
    ],
  },
  {
    id: 'scouter',
    label: 'Scouter',
    // Kein `route`: der Scouter ist ein Dialog innerhalb von Pressespiegel, keine eigene Seite —
    // diese Tour startet ausschließlich über den Hilfe-Button im Scouter-Dialog selbst (siehe
    // ScouterDialog.js), nicht über das globale Hilfe-Menü.
    route: null,
    steps: [
      {
        target: '#scouter-select',
        title: 'Scouter auswählen',
        body: 'Der Scouter durchsucht automatisch News-Suchen nach deinen Suchbegriffen — praktisch, um Medienberichte über ARIWA zu finden, ohne selbst zu suchen. Du kannst mehrere benannte Scouter mit eigenen Suchbegriffen anlegen; hier wechselst du zwischen ihnen.',
      },
      {
        target: '[data-testid="scouter-create-btn"]',
        title: 'Neuen Scouter anlegen',
        body: 'Legt einen weiteren Suchauftrag mit eigenen Suchbegriffen an — z. B. für ein bestimmtes Thema. Er startet pausiert, du kannst ihn danach jederzeit anpassen.',
      },
      {
        target: '[data-testid="scouter-settings-toggle-btn"]',
        title: 'Suchauftrag bearbeiten',
        body: 'Öffnet die Konfiguration des gerade ausgewählten Scouters — Name, Suchbegriffe, Ausschlüsse und Automatik. Ich öffne sie dir gleich mal.',
        // Nur öffnen, wenn noch zu — war sie schon offen, soll die Tour sie nicht zuklappen.
        before: () => {
          const toggle = document.querySelector('[data-testid="scouter-settings-toggle-btn"]');
          if (toggle?.getAttribute('aria-expanded') !== 'true') toggle?.click();
        },
      },
      {
        target: '#scouter-tag',
        title: 'Suchbegriffe',
        body: 'Bis zu 20 Suchbegriffe, jeder wird einzeln als eigene Suchphrase verwendet. Mit Enter oder "Tag hinzufügen" fügst du einen neuen hinzu, mit dem X an einem Begriff entfernst du ihn wieder.',
      },
      {
        target: '#scouter-excluded-domains',
        title: 'Seiten ausschließen',
        body: 'Eine Domain oder URL pro Zeile — Treffer von diesen Seiten werden ausgeblendet (nicht gelöscht). Gilt auch rückwirkend für schon gefundene Treffer; bereits übernommene Pressespiegel-Einträge bleiben aber erhalten.',
      },
      {
        target: '#scouter-auto-search',
        title: 'Automatisch suchen',
        body: 'Schaltet die automatische Suche im gewählten Intervall ein. Ist sie aus, musst du selbst auf "Jetzt suchen" klicken.',
      },
      {
        target: '#scouter-interval',
        title: 'Suchintervall',
        body: 'Wie oft die Automatik nachsehen soll — von stündlich bis wöchentlich.',
      },
      {
        target: '[data-testid="scouter-save-btn"]',
        title: 'Konfiguration speichern',
        body: 'Speichert deine Änderungen. Wichtig: Ohne Speichern startet weder die Automatik noch "Jetzt suchen" mit den neuen Einstellungen.',
      },
      {
        target: '[data-testid="scouter-status-badge"]',
        title: 'Status',
        body: 'Zeigt auf einen Blick, ob gerade gesucht wird, die Automatik aktiv oder pausiert ist, oder ob beim letzten Lauf etwas schiefgegangen ist.',
      },
      {
        target: '[data-testid="scouter-run-btn"]',
        title: 'Jetzt suchen',
        body: 'Startet einen Suchlauf sofort, statt auf die Automatik zu warten. Läuft im Hintergrund — du kannst den Dialog währenddessen schließen.',
      },
      {
        target: '[data-testid="scouter-stats"]',
        title: 'Kennzahlen',
        body: 'Letzter und nächster Lauf sowie die Anzahl neuer Treffer beim letzten Mal.',
      },
      {
        target: '[data-testid="scouter-filters"]',
        title: 'Treffer filtern',
        body: 'Hier grenzt du die Trefferliste ein: nach Status (neu/übernommen/ausgeblendet), danach ob ein passender Pressekontakt gefunden wurde, nach Suchbegriff oder Sortierung. Alle Filter zusammen mit Suche und Zeitraum gelten gemeinsam.',
      },
      {
        target: '[data-testid="scouter-results"]',
        title: 'Trefferliste',
        body: 'Jede Karte ist ein gefundener Artikel. "Kontakt hinzufügen" ordnet eine gefundene Autor*in einem Kontakt zu (oder legt einen neuen an), "Übernehmen" macht daraus einen Pressespiegel-Eintrag, "Ausblenden" versteckt einen unpassenden Treffer wieder.',
      },
    ],
  },
];
